"use client";

/**
 * "Assumed" — the qualifier on a figure whose horizon is the 36-month fallback
 * rather than an explicit `contract_term_years` assumption.
 *
 * It exists so a reader does not take the horizon for a finding (plan §6 risk
 * 1). It used to live inside the label string as an " (assumed)" suffix, which
 * put it last in a one-line ellipsed tile label — so on a 4-up grid the
 * qualifier was reliably the part that got cut. As a badge beside the value it
 * competes with nothing for width.
 *
 * `title` carries the full sentence for pointer users; the description on the
 * figure's row says the same thing in prose for everyone else, so nothing here
 * is hover-only.
 */

export interface ReportAssumedBadgeProps {
  /** e.g. "3 years" — omitted from the tooltip when the horizon is unknown. */
  horizonLabel?: string | null;
}

export function ReportAssumedBadge({ horizonLabel }: ReportAssumedBadgeProps) {
  const tooltip = horizonLabel
    ? `The ${horizonLabel} horizon is assumed, not an explicit contract term.`
    : "This horizon is assumed, not an explicit contract term.";

  return (
    <span
      title={tooltip}
      className={[
        "inline-flex shrink-0 items-center rounded-(--radius-xs)",
        "bg-(--decide-bg) px-1.5 py-px",
        "font-[family-name:var(--font-body)] text-[9px] font-bold",
        "uppercase tracking-[0.07em] text-(--decide)",
      ].join(" ")}
    >
      Assumed
    </span>
  );
}
